import React, { useState, useEffect } from 'react';
import { Container, Card, Row, Col, Button, Table, Badge, Modal, Form, Alert } from 'react-bootstrap';
import { FaMoneyBillWave, FaReceipt, FaEdit, FaSave, FaTimes, FaEye, FaTrash } from 'react-icons/fa';
import { transactionService } from '../services/transactionService';
import { useNavigate } from 'react-router-dom';

const TransactionsManagement = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showTaxModal, setShowTaxModal] = useState(false);
  const [taxRates, setTaxRates] = useState({ gst: 0.18, serviceTax: 0, platformFee: 0.05 });
  const [editingTaxRates, setEditingTaxRates] = useState({ gst: 0.18, serviceTax: 0, platformFee: 0.05 });
  const [saving, setSaving] = useState(false);
  const [users, setUsers] = useState({});
  const [vendors, setVendors] = useState({});
  const [showTransactionModal, setShowTransactionModal] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [bookingDetails, setBookingDetails] = useState(null); // Add state for booking details
  const navigate = useNavigate();

  useEffect(() => {
    fetchTransactions();
    fetchTaxRates();
  }, []);

  const fetchTransactions = async () => {
    try {
      setLoading(true);
      const data = await transactionService.getAllTransactions(100);
      const transactionList = Array.isArray(data) ? data : [];
      setTransactions(transactionList);
      setLoading(false);

      // Populate any missing user/vendor details asynchronously in the background
      fetchUsersAndVendors(transactionList).catch((e) =>
        console.warn("Background user/vendor populating error:", e)
      );

      setError(null);
    } catch (err) {
      console.error('❌ TransactionsManagement Error fetching transactions:', err);
      setError(`Failed to load transactions: ${err.message || 'Unknown error'}`);
      setLoading(false);
    }
  };

  const handleDeleteTransaction = async (transactionId) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this transaction? This action cannot be undone."
    );
    if (!confirmDelete) return;

    try {
      await transactionService.deleteTransaction(transactionId);
      // Remove from UI
      setTransactions(prev => prev.filter(t => t.id !== transactionId));
      // Optional: show success
      setError(null);
    } catch (err) {
      console.error("Delete failed:", err);
      // Show the real error message from backend (e.g. "Transaction not found")
      setError(`Failed to delete transaction: ${err.message}`);
    }
  };

  const fetchUsersAndVendors = async (transactions) => {
    try {
      const userLookup = {};
      const vendorLookup = {};

      // Populate from backend batch payload first
      transactions.forEach((tx) => {
        if (tx.userId && tx.userDetails) userLookup[tx.userId] = tx.userDetails;
        if (tx.vendorId && tx.vendorDetails) vendorLookup[tx.vendorId] = tx.vendorDetails;
      });

      // Fetch only missing records if any
      const missingUserIds = [...new Set(transactions.map((t) => t.userId).filter((id) => id && !userLookup[id]))];
      const missingVendorIds = [...new Set(transactions.map((t) => t.vendorId).filter((id) => id && !vendorLookup[id]))];

      if (missingUserIds.length > 0) {
        const userResults = await Promise.all(
          missingUserIds.map((id) =>
            transactionService.getUserById(id).then((data) => ({ id, data })).catch(() => ({ id, data: null }))
          )
        );
        userResults.forEach((r) => { if (r.data) userLookup[r.id] = r.data; });
      }

      if (missingVendorIds.length > 0) {
        const vendorResults = await Promise.all(
          missingVendorIds.map((id) =>
            transactionService.getVendorById(id).then((data) => ({ id, data })).catch(() => ({ id, data: null }))
          )
        );
        vendorResults.forEach((r) => { if (r.data) vendorLookup[r.id] = r.data; });
      }

      setUsers(userLookup);
      setVendors(vendorLookup);
    } catch (err) {
      console.error('Error fetching user/vendor data:', err);
    }
  };

  const fetchTaxRates = async () => {
    try {
      const rates = await transactionService.getTaxRates();
      setTaxRates(rates);
      setEditingTaxRates(rates);
    } catch (err) {
      console.error('Error fetching tax rates:', err);
      setError('Failed to load tax rates: ' + (err.message || 'Unknown error'));
    }
  };

  const handleSaveTaxRates = async () => {
    try {
      setSaving(true);
      await transactionService.updateTaxRates(editingTaxRates);
      setTaxRates(editingTaxRates);
      setShowTaxModal(false);
      try {
        const { auditLogger } = require('../utils/auditLogger');
        auditLogger.logAction('UPDATE_TAX_RATES', editingTaxRates);
      } catch {}
    } catch (err) {
      console.error('Error updating tax rates:', err);
      setError('Failed to update tax rates');
    } finally {
      setSaving(false);
    }
  };

  const handleExportCSVChunked = () => {
    try {
      if (!transactions || transactions.length === 0) return;
      const headers = "ID,Amount,Status,Date\n";
      const rows = transactions.map(t => `"${t.id || ''}","${t.amount || 0}","${t.status || ''}","${t.createdAt || ''}"`);
      const csvContent = headers + rows.join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', `transactions_export_${Date.now()}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (e) {
      console.error("CSV Export failed:", e);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  const formatDate = (dateInput) => {
    if (!dateInput) return 'N/A';

    try {
      let date = null;

      // Handle timestamp objects {_seconds, _nanoseconds}
      if (dateInput && typeof dateInput === 'object' && dateInput._seconds !== undefined) {
        // Additional validation to ensure _seconds is a number
        if (typeof dateInput._seconds === 'number') {
          date = new Date(dateInput._seconds * 1000);
        }
      }
      // Handle timestamp objects {seconds, nanoseconds}
      else if (dateInput && typeof dateInput === 'object' && dateInput.seconds !== undefined) {
        // Additional validation to ensure seconds is a number
        if (typeof dateInput.seconds === 'number') {
          date = new Date(dateInput.seconds * 1000);
        }
      }
      // Handle standard Date objects
      else if (dateInput instanceof Date) {
        date = dateInput;
      }
      // Handle string dates
      else if (typeof dateInput === 'string') {
        // Check if it's a numeric string (timestamp)
        if (/^\d+$/.test(dateInput)) {
          date = new Date(parseInt(dateInput, 10));
        } else {
          // Try standard date parsing
          date = new Date(dateInput);
        }
      }
      // Handle numeric timestamps
      else if (typeof dateInput === 'number') {
        date = new Date(dateInput);
      }

      // Validate the date
      if (!date || isNaN(date.getTime())) {
        // For debugging, we can see what the actual value is
        console.log('Date formatting failed for:', dateInput);
        return `Invalid: ${String(dateInput)}`;
      }

      // Format the valid date
      return date.toLocaleString('en-IN', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      console.error('Error formatting date:', dateInput, error);
      return `Invalid: ${String(dateInput)}`;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'completed':
        return <Badge bg="success">Completed</Badge>;
      case 'pending':
        return <Badge bg="warning">Pending</Badge>;
      case 'cancelled':
        return <Badge bg="danger">Cancelled</Badge>;
      case 'refunded':
        return <Badge bg="secondary">Refunded</Badge>;
      default:
        return <Badge bg="secondary">{status}</Badge>;
    }
  };

  const getUserName = (userId) => {
    if (!userId) return 'N/A';
    const user = users[userId];
    if (!user) return userId.substring(0, 8) + '...';

    // Extract user name from the data structure
    return user.name || user.email || userId.substring(0, 8) + '...';
  };

  const getVendorName = (vendorId) => {
    if (!vendorId) return 'N/A';
    const vendor = vendors[vendorId];
    if (!vendor) return vendorId.substring(0, 8) + '...';

    // Extract vendor name from the nested structure
    if (vendor.vendor && vendor.vendor.personal_info && vendor.vendor.personal_info.name) {
      return vendor.vendor.personal_info.name;
    }

    // Fallback to businessName or other fields
    return vendor.businessName || vendor.name || vendor.email || vendorId.substring(0, 8) + '...';
  };

  const viewTransactionDetails = async (transaction) => {
    setSelectedTransaction(transaction);

    // If service information is not already in the transaction, fetch booking details
    if (!transaction.service && !transaction.serviceName) {
      try {
        const bookingData = await transactionService.getBookingById(transaction.bookingId);
        if (bookingData) {
          // Update the selected transaction with booking details
          setSelectedTransaction(prev => ({
            ...prev,
            service: bookingData.service || bookingData.serviceName,
            serviceAddress: bookingData.address,
            serviceNotes: bookingData.notes
          }));
        }
      } catch (err) {
        console.error('Error fetching booking details:', err);
      }
    }

    setShowTransactionModal(true);
  };

  if (loading) {
    return (
      <div className="loading-spinner">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <Container fluid className="px-3 px-md-4 py-3">
      <div className="d-flex flex-column flex-sm-row justify-content-between align-items-sm-center gap-2 mb-4">
        <h2 className="mb-0 fw-bold">
          <FaMoneyBillWave className="me-2 text-primary" />
          Transaction Management
        </h2>
      </div>

      {error && (
        <Alert variant="danger" onClose={() => setError(null)} dismissible className="d-flex justify-content-between align-items-center">
          <div>
            <strong>Error: </strong> {error}
          </div>
          <Button variant="outline-danger" size="sm" onClick={() => fetchTransactions()} className="ms-2">
            Retry
          </Button>
        </Alert>
      )}

      <Row className="g-3 mb-4">
        <Col xs={12}>
          <Card className="shadow-sm border-0">
            <Card.Header className="bg-primary text-white d-flex justify-content-between align-items-center py-3">
              <h5 className="mb-0 fw-bold">
                <FaEdit className="me-2" />
                Tax Configuration
              </h5>
              <Button variant="light" size="sm" onClick={() => setShowTaxModal(true)}>
                <FaEdit className="me-1" />
                Edit Rates
              </Button>
            </Card.Header>
            <Card.Body>
              <Row className="g-3 text-center">
                <Col xs={12} sm={4}>
                  <div className="tax-info p-2 rounded bg-light">
                    <h6 className="text-muted small mb-1">GST Rate</h6>
                    <p className="h4 fw-bold text-primary mb-0">{(taxRates.gst * 100).toFixed(2)}%</p>
                  </div>
                </Col>
                <Col xs={12} sm={4}>
                  <div className="tax-info p-2 rounded bg-light">
                    <h6 className="text-muted small mb-1">Service Tax</h6>
                    <p className="h4 fw-bold text-success mb-0">{(taxRates.serviceTax * 100).toFixed(2)}%</p>
                  </div>
                </Col>
                <Col xs={12} sm={4}>
                  <div className="tax-info p-2 rounded bg-light">
                    <h6 className="text-muted small mb-1">Platform Fee</h6>
                    <p className="h4 fw-bold text-info mb-0">{(taxRates.platformFee * 100).toFixed(2)}%</p>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row>
        <Col xs={12}>
          <Card className="shadow-sm border-0">
            <Card.Header className="bg-secondary text-white py-3">
              <h5 className="mb-0 fw-bold">
                <FaReceipt className="me-2" />
                All Transactions
              </h5>
            </Card.Header>
            <Card.Body className="p-0">
              <Table responsive hover className="align-middle text-nowrap mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Transaction ID</th>
                    <th>Booking ID</th>
                    <th>User Name</th>
                    <th>Vendor Name</th>
                    <th>Service</th>
                    <th>Total Amount</th>
                    <th>Date & Time</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.length === 0 ? (
                    <tr>
                      <td colSpan="9" className="text-center">
                        No transactions found
                      </td>
                    </tr>
                  ) : (
                    transactions.map((transaction) => (
                      <tr key={transaction.id}>
                        <td>
                          <small className="text-muted">{transaction.id?.substring(0, 8)}...</small>
                        </td>
                        <td>
                          <small className="text-muted">{transaction.bookingId?.substring(0, 8)}...</small>
                        </td>
                        <td>{getUserName(transaction.userId)}</td>
                        <td>{getVendorName(transaction.vendorId)}</td>
                        <td>
                          <small className="text-muted">
                            {transaction.service || 'View details for service'}
                          </small>
                        </td>
                        <td>
                          <strong>{formatCurrency(transaction.amount)}</strong>
                        </td>
                        <td>{formatDate(transaction.createdAt || transaction.created_at || transaction.date)}</td>
                        <td>{getStatusBadge(transaction.status)}</td>
                        <td className="d-flex gap-2">
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => viewTransactionDetails(transaction)}
                          >
                            <FaEye />
                          </Button>

                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleDeleteTransaction(transaction.id)}
                          >
                            <FaTrash />
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Tax Rates Modal */}
      <Modal show={showTaxModal} onHide={() => setShowTaxModal(false)} centered>
        <Modal.Header closeButton className="bg-light">
          <Modal.Title>
            <FaEdit className="me-2" />
            Edit Tax Rates
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>GST Rate (%)</Form.Label>
              <Form.Control
                type="number"
                step="0.01"
                min="0"
                max="100"
                value={(editingTaxRates.gst * 100).toFixed(2)}
                onChange={(e) =>
                  setEditingTaxRates({
                    ...editingTaxRates,
                    gst: parseFloat(e.target.value) / 100
                  })
                }
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Service Tax (%)</Form.Label>
              <Form.Control
                type="number"
                step="0.01"
                min="0"
                max="100"
                value={(editingTaxRates.serviceTax * 100).toFixed(2)}
                onChange={(e) =>
                  setEditingTaxRates({
                    ...editingTaxRates,
                    serviceTax: parseFloat(e.target.value) / 100
                  })
                }
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Platform Fee (%)</Form.Label>
              <Form.Control
                type="number"
                step="0.01"
                min="0"
                max="100"
                value={(editingTaxRates.platformFee * 100).toFixed(2)}
                onChange={(e) =>
                  setEditingTaxRates({
                    ...editingTaxRates,
                    platformFee: parseFloat(e.target.value) / 100
                  })
                }
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowTaxModal(false)}>
            <FaTimes className="me-1" />
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSaveTaxRates} disabled={saving}>
            {saving ? (
              <>
                <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                Saving...
              </>
            ) : (
              <>
                <FaSave className="me-1" />
                Save Changes
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Transaction Details Modal */}
      <Modal show={showTransactionModal} onHide={() => setShowTransactionModal(false)} size="lg" centered>
        <Modal.Header closeButton className="bg-light">
          <Modal.Title>
            <FaReceipt className="me-2" />
            Transaction Details
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedTransaction && (
            <div className="transaction-details">
              <Row className="mb-3">
                <Col md={6}>
                  <h6>Transaction Information</h6>
                  <p><strong>ID:</strong> {selectedTransaction.id}</p>
                  <p><strong>Booking ID:</strong> {selectedTransaction.bookingId}</p>
                  <p><strong>Status:</strong> {getStatusBadge(selectedTransaction.status)}</p>
                  <p><strong>Date & Time:</strong> {formatDate(selectedTransaction.createdAt)}</p>
                  {selectedTransaction.completedAt && (
                    <p><strong>Completed Date:</strong> {formatDate(selectedTransaction.completedAt)}</p>
                  )}
                  {selectedTransaction.updatedAt && (
                    <p><strong>Last Updated:</strong> {formatDate(selectedTransaction.updatedAt)}</p>
                  )}
                </Col>
                <Col md={6}>
                  <h6>Vendor Information</h6>
                  <p><strong>Vendor ID:</strong> {selectedTransaction.vendorId}</p>
                  <p><strong>Name:</strong> {getVendorName(selectedTransaction.vendorId)}</p>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={6}>
                  <h6>Financial Details</h6>
                  <p><strong>Base Amount:</strong> {formatCurrency(selectedTransaction.baseAmount)}</p>
                  <p><strong>GST:</strong> {formatCurrency(selectedTransaction.gstAmount)}</p>
                  <p><strong>Service Tax:</strong> {formatCurrency(selectedTransaction.serviceTaxAmount)}</p>
                  <p><strong>Platform Fee:</strong> {formatCurrency(selectedTransaction.platformFeeAmount)}</p>
                  {selectedTransaction.tipAmount > 0 && (
                    <p><strong>Tip:</strong> {formatCurrency(selectedTransaction.tipAmount)}
                      {selectedTransaction.tipType && (
                        <Badge bg="info" className="ms-2">{selectedTransaction.tipType}</Badge>
                      )}
                    </p>
                  )}
                  <p><strong>Total Amount:</strong> <strong>{formatCurrency(selectedTransaction.amount)}</strong></p>
                </Col>
                <Col md={6}>
                  <h6>User Information</h6>
                  <p><strong>User ID:</strong> {selectedTransaction.userId}</p>
                  <p><strong>Name:</strong> {getUserName(selectedTransaction.userId)}</p>
                </Col>
                <Col md={6}>
                  <h6>Service Information</h6>
                  {(selectedTransaction.service || selectedTransaction.serviceName) && (
                    <p><strong>Service:</strong> {selectedTransaction.service || selectedTransaction.serviceName}</p>
                  )}
                  {selectedTransaction.serviceAddress && (
                    <p><strong>Address:</strong> {selectedTransaction.serviceAddress}</p>
                  )}
                  {selectedTransaction.serviceNotes && (
                    <p><strong>Notes:</strong> {selectedTransaction.serviceNotes}</p>
                  )}
                  {!selectedTransaction.service && !selectedTransaction.serviceName && !selectedTransaction.serviceAddress && !selectedTransaction.serviceNotes && (
                    <p className="text-muted">No service information available</p>
                  )}
                </Col>
              </Row>

              {selectedTransaction.metadata && (
                <Row>
                  <Col md={12}>
                    <h6>Additional Information</h6>
                    <pre>{JSON.stringify(selectedTransaction.metadata, null, 2)}</pre>
                  </Col>
                </Row>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowTransactionModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      <style jsx>{`
        .tax-info h6 {
          margin-bottom: 0.5rem;
          font-weight: 600;
        }
        .tax-info p {
          margin-bottom: 0;
        }
        .transaction-details h6 {
          border-bottom: 1px solid #eee;
          padding-bottom: 0.5rem;
          margin-bottom: 1rem;
        }
      `}</style>
    </Container>
  );
};

export default TransactionsManagement;